import {
  Action,
  ActionSubject,
  TrackAction,
  TrackActionSubject,
  type FeatureEvents,
  type ScreenEvent,
  type TrackEvent,
  type UIEvent,
} from '../new-analytics-types';

const unifiedTeamProfileScreen = 'unifiedTeamProfileScreen';

type ScreenKeys =
  | 'unifiedTeamProfileScreen'
  | 'addTeamModalScreen'
  | 'editTeamNameModalScreen'
  | 'editTeamDescriptionModalScreen'
  | 'deleteTeamModalScreen'
  | 'removeMemberFromTeamModalScreen'
  | 'teamPendingRequestsTabScreen';

type UiKeys =
  | 'addMembersToTeamButtonClicked'
  | 'dialogOpenedTeamDeletion'
  | 'removeMemberFromTeamButtonClicked'
  | 'addMembersToTeamModalCancelButtonClicked'
  | 'dialogClosedTeamDeletion'
  | 'removeMemberFromTeamModalCancelButtonClicked'
  | 'removeMemberFromTeamConfirmButtonClicked'
  | 'addTeamModalConfirmButtonClicked'
  | 'deleteTeamConfirmButtonClicked'
  | 'editTeamNameButtonClicked'
  | 'editTeamDescriptionButtonClicked'
  | 'editTeamNameModalCancelButtonClicked'
  | 'editTeamDescriptionModalCancelButtonClicked'
  | 'editTeamNameSubmitButtonClicked'
  | 'editTeamDescriptionSubmitButtonClicked'
  | 'acceptJoinRequestButtonClicked'
  | 'declineJoinRequestButtonClicked'
  | 'teamMemberProfileLinkClicked';

type TrackKeys =
  | 'memberAdded'
  | 'memberRemoved'
  | 'teamNameEdited'
  | 'teamDescriptionEdited'
  | 'pendingMemberAccepted'
  | 'pendingMemberDenied'
  | 'teamDeleted';

export const unifiedTeamProfileEvents: FeatureEvents<ScreenKeys, UiKeys, TrackKeys, never> = {
  confluenceSpec: 'https://hello.atlassian.net/wiki/spaces/PTC/pages/5860493122/Analytics+Spec+Teams+Profile',
  screen: {
    unifiedTeamProfileScreen: ({
      teamId,
      memberCount,
      isVerified,
      isVortex,
    }: {
      teamId: string;
      memberCount: number;
      isVerified: boolean;
      isVortex: boolean;
    }): ScreenEvent => ({
      data: {
        name: unifiedTeamProfileScreen,
        attributes: {
          teamId,
          memberCount,
          isVerified,
          isVortex,
        },
      },
      dataPortalLink: '',
    }),
    addTeamModalScreen: ({ teamId, isVortex }: { teamId: string; isVortex: boolean }): ScreenEvent => ({
      data: {
        name: 'addTeamModalScreen',
        attributes: {
          teamId,
          isVortex,
        },
      },
      dataPortalLink: '',
    }),
    editTeamNameModalScreen: ({
      teamId,
      isVortex,
      isVerified,
    }: {
      teamId: string;
      isVortex: boolean;
      isVerified: boolean;
    }): ScreenEvent => ({
      data: {
        name: 'editTeamNameModalScreen',
        attributes: {
          teamId,
          isVortex,
          isVerified,
        },
      },
      dataPortalLink: '',
    }),
    editTeamDescriptionModalScreen: ({
      teamId,
      isVortex,
      isVerified,
    }: {
      teamId: string;
      isVortex: boolean;
      isVerified: boolean;
    }): ScreenEvent => ({
      data: {
        name: 'editTeamDescriptionModalScreen',
        attributes: {
          teamId,
          isVortex,
          isVerified,
        },
      },
      dataPortalLink: '',
    }),
    deleteTeamModalScreen: ({ teamId, isVortex, isVerified }: { teamId: string; isVortex: boolean; isVerified: boolean }): ScreenEvent => ({
      data: {
        name: 'deleteTeamModalScreen',
        attributes: {
          teamId,
          isVortex,
          isVerified,
        },
      },
      dataPortalLink: '',
    }),
    removeMemberFromTeamModalScreen: ({
      teamId,
      memberId,
      isVortex,
    }: {
      teamId: string;
      memberId: string;
      isVortex: boolean;
    }): ScreenEvent => ({
      data: {
        name: 'removeMemberFromTeamModalScreen',
        attributes: {
          teamId,
          memberId,
          isVortex,
        },
      },
      dataPortalLink: '',
    }),
    teamPendingRequestsTabScreen: ({
      teamId,
      isVortex,
      pendingRequestCount,
    }: {
      teamId: string;
      isVortex: boolean;
      pendingRequestCount?: number;
    }): ScreenEvent => ({
      data: {
        name: 'teamPendingRequestsTabScreen',
        attributes: {
          teamId,
          isVortex,
          pendingRequestCount,
        },
      },
      dataPortalLink: '',
    }),
  },
  ui: {
    addMembersToTeamButtonClicked: ({ teamId, isVortex }: { teamId: string; isVortex: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'addTeamMembers',
        source: unifiedTeamProfileScreen,
        attributes: {
          teamId,
          isVortex,
        },
      },
      dataPortalLink: '',
    }),
    dialogOpenedTeamDeletion: (attributes: { teamId: string; isVortex: boolean; isVerified: boolean }): UIEvent => ({
      data: {
        action: Action.Opened,
        actionSubject: ActionSubject.Dialog,
        actionSubjectId: 'teamDeletion',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    removeMemberFromTeamButtonClicked: (attributes: { teamId: string; memberId: string; isVortex: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'removeTeamMember',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    addMembersToTeamModalCancelButtonClicked: (attributes: { teamId: string; isVortex: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'addMembersToTeamModalCancel',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    dialogClosedTeamDeletion: (attributes: { teamId: string; isVortex: boolean; isVerified: boolean }): UIEvent => ({
      data: {
        action: Action.Closed,
        actionSubject: ActionSubject.Dialog,
        actionSubjectId: 'teamDeletion',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    removeMemberFromTeamModalCancelButtonClicked: (attributes: { teamId: string; memberId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'removeMemberFromTeamModalCancel',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    removeMemberFromTeamConfirmButtonClicked: (attributes: { teamId: string; memberId: string; isVortex: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'removeMemberFromTeamConfirm',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    addTeamModalConfirmButtonClicked: (attributes: { teamId: string; memberCount: number; isVortex: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'addTeamModalConfirm',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    deleteTeamConfirmButtonClicked: (attributes: { teamId: string; isVortex: boolean; isVerified: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'deleteTeamConfirm',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    editTeamNameButtonClicked: (attributes: { teamId: string; isVortex: boolean; isVerified: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'editTeamName',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    editTeamDescriptionButtonClicked: (attributes: { teamId: string; isVortex: boolean; isVerified: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'editTeamDescription',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    editTeamNameModalCancelButtonClicked: (attributes: { teamId: string; isVortex: boolean; isVerified: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'editTeamNameModalCancel',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    editTeamDescriptionModalCancelButtonClicked: (attributes: { teamId: string; isVortex: boolean; isVerified: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'editTeamDescriptionModalCancel',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    editTeamNameSubmitButtonClicked: (attributes: { teamId: string; isVortex: boolean; isVerified: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'editTeamNameSubmit',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    editTeamDescriptionSubmitButtonClicked: (attributes: { teamId: string; isVortex: boolean; isVerified: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'editTeamDescriptionSubmit',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    acceptJoinRequestButtonClicked: (attributes: { teamId: string; requesterId: string; isVortex: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'acceptJoinRequest',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    declineJoinRequestButtonClicked: (attributes: { teamId: string; requesterId: string; isVortex: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'declineJoinRequest',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
    teamMemberProfileLinkClicked: (attributes: { teamId: string; memberId: string; url: string; isVortex: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Link,
        actionSubjectId: 'teamMemberProfileLink',
        source: unifiedTeamProfileScreen,
        attributes,
      },
      dataPortalLink: '',
    }),
  },
  track: {
    memberAdded: ({ teamId, isVortex, members }: { teamId: string; isVortex: boolean; members: number }): TrackEvent => ({
      data: {
        action: TrackAction.Sent,
        actionSubject: TrackActionSubject.TeamInvitation,
        source: unifiedTeamProfileScreen,
        attributes: {
          teamId,
          isVortex,
          members,
        },
      },
      dataPortalLink: '',
    }),
    memberRemoved: ({
      teamId,
      memberId,
      isVortex,
      teamSize,
    }: {
      teamId: string;
      memberId: string;
      isVortex: boolean;
      teamSize: number;
    }): TrackEvent => ({
      data: {
        action: TrackAction.Removed,
        actionSubject: TrackActionSubject.TeamMember,
        source: unifiedTeamProfileScreen,
        attributes: {
          teamId,
          memberId,
          isVortex,
          teamSize,
        },
      },
      dataPortalLink: '',
    }),
    teamNameEdited: ({ teamId, isVortex, isVerified }: { teamId: string; isVortex: boolean; isVerified: boolean }): TrackEvent => ({
      data: {
        action: TrackAction.Edited,
        actionSubject: TrackActionSubject.TeamProfileName,
        source: unifiedTeamProfileScreen,
        attributes: {
          teamId,
          isVortex,
          isVerified,
        },
      },
      dataPortalLink: '',
    }),
    teamDescriptionEdited: ({ teamId, isVortex, isVerified }: { teamId: string; isVortex: boolean; isVerified: boolean }): TrackEvent => ({
      data: {
        action: TrackAction.Edited,
        actionSubject: TrackActionSubject.TeamProfileDescription,
        source: unifiedTeamProfileScreen,
        attributes: {
          teamId,
          isVortex,
          isVerified,
        },
      },
      dataPortalLink: '',
    }),
    pendingMemberAccepted: ({ teamId, memberId, isVortex }: { teamId: string; memberId: string; isVortex: boolean }): TrackEvent => ({
      data: {
        action: TrackAction.Accepted,
        actionSubject: TrackActionSubject.JoinRequest,
        source: unifiedTeamProfileScreen,
        attributes: {
          teamId,
          memberId,
          isVortex,
        },
      },
      dataPortalLink: '',
    }),
    pendingMemberDenied: ({ teamId, memberId, isVortex }: { teamId: string; memberId: string; isVortex: boolean }): TrackEvent => ({
      data: {
        action: TrackAction.Denied,
        actionSubject: TrackActionSubject.JoinRequest,
        source: unifiedTeamProfileScreen,
        attributes: {
          teamId,
          memberId,
          isVortex,
        },
      },
      dataPortalLink: '',
    }),
    teamDeleted: ({
      teamId,
      isVortex,
      isVerified,
      memberCount,
    }: {
      teamId: string;
      isVortex: boolean;
      isVerified: boolean;
      memberCount: number;
    }): TrackEvent => ({
      data: {
        action: TrackAction.Removed,
        actionSubject: TrackActionSubject.Team,
        source: unifiedTeamProfileScreen,
        attributes: {
          teamId,
          isVortex,
          isVerified,
          memberCount,
        },
      },
      dataPortalLink: '',
    }),
  },
  operational: {},
};
